<template>
  <TemplateManager
    v-model:selected-template="selectedColumnTemplate"
    v-model:options="customizableColumns"
    v-model:add-template="add"
    v-model:new-template-name="newTemplateName"
    :templates="columnTemplates"
    :modal
    @template-changed="loadColumns(false)"
    @save-template="saveTemplate"
    @delete-template="deleteTemplate"
    @set-default="defaultTemplate"
    @options-changed="orderColumnLists"
    @new-template="newTemplate"
    @try-confirm="tryConfirm"
  ></TemplateManager>
</template>

<script setup lang="ts">
import { BudgetColumns } from "@/enums/BudgetColumns";
import { SubdivisionColumns } from "@/enums/SubdivisionColumns";
import { SettingScope } from "@/enums/SystemSettings";
import { packBit, testBit } from "@/services/Utility";
import { CustomColumnTemplateListSchema, type CustomColumnTemplate, type CustomizableColumn } from "@/types/CustomizableColumns";
import { useConfirm } from "primevue/useconfirm";

const customizableColumns = ref<CustomizableColumn[][]>([[], []]);
const columnTemplates = ref<CustomColumnTemplate[]>([]);
const selectedColumnTemplate = ref<CustomColumnTemplate>();
const add = ref(false);
const newTemplateName = ref<string>("");

interface Props {
  modal?: boolean;
  initialRows: number | null;
  scope: SettingScope;
}
const props = withDefaults(defineProps<Props>(), {
  modal: false,
});

const emit = defineEmits<{
  confirm: [columns: number];
}>();

const confirm = useConfirm();

onBeforeMount(async (): Promise<void> => {
  await loadColumnTemplates(true);
});

const orderColumnLists = (): void => {
  customizableColumns.value[0].sort((a, b) => a.label.localeCompare(b.label));
  customizableColumns.value[1].sort((a, b) => a.label.localeCompare(b.label));
};

const loadColumnTemplates = async (initialization: boolean): Promise<void> => {
  await axios
    .get("/settings/columns/templates/" + props.scope)
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          parseData(CustomColumnTemplateListSchema, data, columnTemplates);
        },
        (errors: ApiError[]) => {
          error("An error occurred", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));

  let previousTemplate = selectedColumnTemplate.value ? selectedColumnTemplate.value.settingId : null;

  selectedColumnTemplate.value = undefined;

  for (let template of columnTemplates.value) {
    if (previousTemplate) {
      if (template.settingId === previousTemplate) {
        selectedColumnTemplate.value = template;
      }
    } else if (template.defaultTemplate && props.initialRows == null) {
      selectedColumnTemplate.value = template;
    }
  }

  loadColumns(initialization);
};

const loadColumns = (initialization: boolean): void => {
  customizableColumns.value[0] = [];
  customizableColumns.value[1] = [];

  let columns = 0;
  if (props.initialRows != null && initialization) columns = props.initialRows;
  else if (selectedColumnTemplate.value != null) columns = selectedColumnTemplate.value.columns;

  var cols;
  if (props.scope == SettingScope.BUDGET_COLUMN_TEMPLATES) cols = BudgetColumns;
  else cols = SubdivisionColumns;
  Object.entries(cols).forEach((col) => {
    if (col[1].alwaysVisible) return;
    if (col[1].bit == null) return;
    if (testBit(columns, col[1].bit)) customizableColumns.value[1].push(col[1]);
    else customizableColumns.value[0].push(col[1]);
  });

  orderColumnLists();
};

const saveTemplate = (): void => {
  if (!selectedColumnTemplate.value) return;

  axios.post("/settings/columns/templates/update", { id: selectedColumnTemplate.value.settingId, columns: getColumnsValue() }).then((response) =>
    handleApiResponse(
      response,
      (data: any) => {
        loadColumnTemplates(false);
        success("Success", "Column template " + selectedColumnTemplate.value?.name + " updated");
      },
      (errors: ApiError[]) => {}
    )
  );
};

const newTemplate = (): void => {
  const columns = getColumnsValue();
  axios.post("/settings/columns/templates/add", { name: newTemplateName.value, columns: columns, scope: props.scope }).then((response) =>
    handleApiResponse(
      response,
      (data: any) => {
        selectedColumnTemplate.value = { settingId: data, name: newTemplateName.value, defaultTemplate: false, columns: columns };
        add.value = false;
        newTemplateName.value = "";
        loadColumnTemplates(false);
        success("Success", "Column template " + newTemplateName.value + " added");
      },
      (errors: ApiError[]) => {}
    )
  );
};

const getColumnsValue = (): number => {
  let value = 0;

  customizableColumns.value[1].forEach((col) => {
    value = packBit(value, true, col.bit);
  });

  return value;
};

const deleteTemplate = (): void => {
  if (!selectedColumnTemplate.value) return;

  let header = "Delete template?";
  let message = "Are you sure you want to delete the column template " + selectedColumnTemplate.value!.name + "?";
  let onReject = () => {
    error("Action cancelled", "Template not deleted");
  };

  confirmPopup(confirm, header, message, deleteTemplateProcess, onReject);
};

const deleteTemplateProcess = (): void => {
  axios.delete("/settings/columns/templates/delete/" + selectedColumnTemplate.value!.settingId).then((response) =>
    handleApiResponse(
      response,
      (data: any) => {
        success("Success", "Column template " + selectedColumnTemplate.value!.name + " deleted");
        selectedColumnTemplate.value = undefined;
        loadColumnTemplates(false);
      },
      (errors: ApiError[]) => {}
    )
  );
};

const defaultTemplate = (): void => {
  if (!selectedColumnTemplate.value) return;

  let header = "Set Default Template?";
  let message = "Are you sure you want to set the default column template to " + selectedColumnTemplate.value!.name + "?";
  let onReject = () => {
    error("Action cancelled", "Default template unchanged");
  };

  confirmPopup(confirm, header, message, setDefaultTemplate, onReject);
};

const setDefaultTemplate = (): void => {
  if (!selectedColumnTemplate.value) return;

  let data = new FormData();
  data.append("id", selectedColumnTemplate.value.settingId + "");

  axios.post("/settings/columns/templates/setdefault", data).then((response) =>
    handleApiResponse(
      response,
      (data: any) => {
        loadColumnTemplates(false);
        success("Success", "Default column template set to " + selectedColumnTemplate.value?.name);
      },
      (errors: ApiError[]) => {}
    )
  );
};

const tryConfirm = (): void => {
  confirmPopup(
    confirm,
    "Confirm Columns?",
    "Are you sure that you want to confirm the selected column?",
    () => emit("confirm", getColumnsValue()),
    () => info("Action Cancelled", "Columns not confirmed")
  );
};
</script>
